import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  Legend,
  type ChartOptions,
  type TooltipItem,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Divider,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';

import {
  useSummarySalesByPeriod,
  type SalesPreset,
} from '@queries/summaries/useSummarySalesByPeriod';
import { formatApiDateToKst } from '@utils/time';

// Explicitly register the controller to avoid runtime errors in production builds.
ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend);

type PresetGroup = 'RANGE' | 'TO_DATE';

const RANGE_PRESETS: SalesPreset[] = ['7D', '30D', '90D', '12M'];
const TO_DATE_PRESETS: SalesPreset[] = ['WTD', 'MTD', 'YTD'];

const PRESET_LABEL: Record<SalesPreset, string> = {
  '7D': 'Last 7 days',
  '30D': 'Last 30 days',
  '90D': 'Last 90 days',
  '12M': 'Last 12 months',
  WTD: 'Week to date',
  MTD: 'Month to date',
  YTD: 'Year to date',
};

function groupFromPreset(preset: SalesPreset): PresetGroup {
  return TO_DATE_PRESETS.includes(preset) ? 'TO_DATE' : 'RANGE';
}

export default function DashBoard() {
  const theme = useTheme();

  // Use a compact control (Select) on narrow screens to avoid overly dense toggles.
  const isDownSm = useMediaQuery((t: any) => t.breakpoints.down('sm'));

  const [preset, setPreset] = useState<SalesPreset>('7D');
  const [group, setGroup] = useState<PresetGroup>(() => groupFromPreset('7D'));

  const { data: salesByPeriod, periodUnit } = useSummarySalesByPeriod(preset);

  // Keep a stable reference for rows to satisfy exhaustive-deps and avoid recomputations.
  const rows = useMemo(() => salesByPeriod?.data ?? [], [salesByPeriod?.data]);

  // Axis labels and tooltip timestamps are always displayed in KST for consistency.
  const labels = useMemo(() => {
    const fmt = preset === '12M' || preset === 'YTD' ? 'YY.MM' : 'MM.DD';
    return rows.map((r) => formatApiDateToKst(r.from, fmt));
  }, [rows, preset]);

  const values = useMemo(() => rows.map((r) => Number(r.amount ?? 0)), [rows]);

  // Give the chart a bit of headroom so bars do not touch the top edge.
  const suggestedMax = useMemo<number | undefined>(() => {
    if (!values.length) return undefined;
    const max = Math.max(...values);
    return Math.ceil(max * 1.1);
  }, [values]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: 'Sales',
          data: values,
          borderRadius: 6,
          categoryPercentage: 0.9,
          barPercentage: 0.9,
          maxBarThickness: preset === '7D' ? 48 : 32,
          backgroundColor: `${theme.palette.primary.main}66`,
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
      ],
    }),
    [labels, values, theme.palette.primary.main, preset]
  );

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 4, right: 8, bottom: 0, left: 0 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<'bar'>[]) => {
              const idx = items?.[0]?.dataIndex ?? 0;
              const row = rows[idx];
              if (!row) return '';

              const from = formatApiDateToKst(row.from, 'YYYY-MM-DD HH:mm:ss');
              const to = formatApiDateToKst(row.to, 'YYYY-MM-DD HH:mm:ss');
              return `${from} ~ ${to} (KST)`;
            },
            label: (ctx: TooltipItem<'bar'>) => {
              const v = Number(ctx.parsed?.y ?? 0);
              return `USD ${v.toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          offset: true,
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: preset === '7D' ? 7 : preset === '30D' ? 10 : 12,
          },
        },
        y: {
          beginAtZero: true,
          grace: '10%',
          suggestedMax,
          ticks: {
            callback: (v) => Number(v).toLocaleString(),
          },
        },
      },
    }),
    [rows, suggestedMax, preset]
  );

  const presetOptions = useMemo(() => {
    const list = group === 'RANGE' ? RANGE_PRESETS : TO_DATE_PRESETS;
    return list.map((p) => ({ value: p, label: PRESET_LABEL[p] }));
  }, [group]);

  // Keep the selected preset valid when switching between preset groups.
  const handleGroupChange = (next: PresetGroup) => {
    setGroup(next);

    const allowed = next === 'RANGE' ? RANGE_PRESETS : TO_DATE_PRESETS;
    if (!allowed.includes(preset)) setPreset(allowed[0]);
  };

  return (
    <Stack spacing={1} sx={{ height: '100%' }}>
      {/* Controls */}
      <Stack spacing={1}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={0.5}
        >
          <Typography variant="body2" color="text.secondary">
            {PRESET_LABEL[preset]} · Bucket: {periodUnit} · Timezone: KST
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <ToggleButtonGroup
            value={group}
            exclusive
            size="small"
            onChange={(_, v) => v && handleGroupChange(v)}
            sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1.25 } }}
          >
            <ToggleButton value="RANGE">Range</ToggleButton>
            <ToggleButton value="TO_DATE">To date</ToggleButton>
          </ToggleButtonGroup>

          {/* On small screens, use a single-line Select for better readability. */}
          {isDownSm ? (
            <Select
              size="small"
              value={preset}
              onChange={(e) => setPreset(e.target.value as SalesPreset)}
              sx={{ minWidth: 220 }}
            >
              {presetOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <ToggleButtonGroup
              value={preset}
              exclusive
              size="small"
              onChange={(_, v) => v && setPreset(v)}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': { px: 1.25, whiteSpace: 'nowrap' },
              }}
            >
              {(group === 'RANGE' ? RANGE_PRESETS : TO_DATE_PRESETS).map((p) => (
                <ToggleButton key={p} value={p}>
                  {PRESET_LABEL[p]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Stack>

        <Divider />
      </Stack>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Chart type="bar" data={data} options={options} />
      </Box>
    </Stack>
  );
}
